import { Component, computed, OnDestroy, Signal, inject, TemplateRef, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { PlaygroundService, PlaygroundRunInfo, EndpointCatalogService, NotificationService, SessionService, CikisiResourcesService } from '../../../core/services';
import { apiErrorMessage } from '../../../core/utils/api-error-message';
import { EndpointDefinition, getEndpointDefinition } from '../../../core/models';

import { SearchResult } from '../models/search-result.model';
import { buildCodeSnippets, CodeLanguage } from '../utils/code-snippet';
import { estimateEndpointPrice, PriceEstimate } from '../utils/endpoint-pricing';
import { PlaygroundQueryPage } from '../utils/playground-query-page';
import { buildCikisiFields } from '../utils/cikisi-fields';
import { FieldMeta, RESULT_FIELD_META, buildFieldsGroup, flattenFields, SORT_OPTIONS } from '../utils/result-fields';
import { QUERY_PLACEHOLDER } from '../utils/query-placeholder';
import { isPhoneViewport } from '../../../core/utils/phone';
import { openHelpSheet } from '../help-sheet/help-sheet';
import { openDateSheet } from '../../../shared/date-sheet/date-sheet';
import { openCikisiFilterSheet } from '../cikisi-filter-sheet/cikisi-filter-sheet';
import { ModalService } from '../../../shared/modal';
import {
  MilaMultiSelectFetcher,
  MilaMultiSelectOption,
} from '../../../shared/mila-multi-select/mila-multi-select-option.model';
import { PlaygroundKeyService } from '../utils/playground-key.service';

export interface FilterRowMeta {
  id: string;
  label: string;
  placeholder: string;
  pricingParam: string;
  payloadField: string;
  /** 'text' = Knowledge Base one per account, nothing to look up, no body
   *  at all; 'flat' = backed by MilaMultiSelect via CikisiResourcesService
   *  all six lookup resources are flat, none hierarchical. */
  kind: 'text' | 'flat';
}

export interface CalState {
  open: boolean;
  year: number;
  month: number;
  selected: Date | null;
}

export type BoolOp = 'OR' | 'AND';

@Component({
  selector: 'app-similar',
  standalone: false,
  templateUrl: './similar.html',
  styleUrl: './similar.scss',
})
export class Similar implements OnDestroy, PlaygroundQueryPage {
  constructor(
    private fb: FormBuilder,
    private playgroundService: PlaygroundService,
    private catalogService: EndpointCatalogService,
    private notificationService: NotificationService,
    private lookupService: CikisiResourcesService,
    private sessionService: SessionService,
    private modalService: ModalService,
  ) {
    // authGuard already resolved /me before this page can render, so the
    // real organization is available synchronously here see Settings/Home
    // for the same convention. Cikisi-only filtering only makes sense (and
    // is only sent) for orgs actually flagged as Cikisi users.
    this.isCikisiUser = this.sessionService.organization?.isCikisiUser ?? false;

    this.form = this.buildForm();

    // Resolved from the live catalog (GET /endpoints) same as /rag,
    // /answer, /search estimateEndpointPrice() falls back to a zero
    // estimate if it's ever missing see endpoint-catalog.model.ts.
    this.endpointDef = computed(() => getEndpointDefinition(this.catalogService.catalog(), 'similar'));


    // One fetcher per 'flat'/'hierarchy' row id see FILTER_ROW_META and
    // CikisiResourcesService for what each actually hits.
    this.flatFetchers = {
      'si-tags': this.lookupService.searchTags,
      'si-sc': this.lookupService.searchSourceCategories,
      'si-ss': this.lookupService.searchSavedSearches,
      'si-si': this.lookupService.searchSourceIds,
      'si-kt': this.lookupService.searchKnowledgeTopics,
      'si-th': this.lookupService.searchThesaurusClasses,
    };
  }

  readonly flatFetchers: Record<string, MilaMultiSelectFetcher>;

  /** Gates the whole "Search only Cikisi content" section see similar.html. */
  readonly isCikisiUser: boolean;

  private readonly _destroy$ = new Subject<void>();

  readonly endpointDef: Signal<EndpointDefinition | undefined>;

  isLoading = false;
  status: 'idle' | 'ok' | 'error' = 'idle';
  /** Same shape as /search (`results` of articles + `meta`), so the same
   *  model and the same item-card view (resultView 'search'). */
  response: SearchResult | null = null;
  latencyMs: number | null = null;
  credits: number | null = null;
  runInfo: PlaygroundRunInfo | null = null;
  errorMessage: string | null = null;
  /** The include_* flags the run in flight sent, so the skeleton matches. */
  loadingFields: ReadonlySet<string> = new Set();
  readonly resultView = 'search' as const;
  readonly emptyHint = 'The result will include similar articles, with the fields you picked in Response configuration.';

  activeLanguage: CodeLanguage = 'ruby';
  copied = false;

  // ── Collapsible sections Response starts collapsed, it's set once and
  // rarely revisited; the other two are what changes query to query. ──
  sectionOpen: Record<'question' | 'request' | 'response', boolean> = {
    question: true,
    request: true,
    response: false,
  };

  toggleSection(key: keyof Similar['sectionOpen']): void {
    this.sectionOpen[key] = !this.sectionOpen[key];
  }

  /** The key runs bill to: picked once in the run bar (playground.html). */
  private readonly apiKey = inject(PlaygroundKeyService);

  // ── "Discover Boolean operators" reference panel ────────────────────
  operatorsHelpOpen = false;

  @ViewChild('helpRows') private helpRows!: TemplateRef<unknown>;

  toggleOperatorsHelp(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Phones get the same rows as a bottom sheet instead of a popover.
    if (isPhoneViewport()) {
      this.closeAll();
      openHelpSheet(this.modalService, { title: 'Boolean search operators', content: this.helpRows });
      return;
    }
    const wasOpen = this.operatorsHelpOpen;
    this.closeAll();
    if (!wasOpen) {
      this.operatorsHelpOpen = true;
    }
  }

  // Same content-filter rows, same param names, as /rag, /answer, /search and /image.
  readonly FILTER_ROW_META: FilterRowMeta[] = [
    // Its picker is disabled while the separate "Knowledge Base" checkbox
    // (see toggleKnowledgeBase()) is on searching the whole knowledge
    // base makes picking specific topics moot.
    { id: 'si-kt',   label: 'Knowledge Topics',    placeholder: 'Search knowledge topics…', pricingParam: 'knowledge_topics',   payloadField: 'knowledge_topics',   kind: 'flat' },
    { id: 'si-tags', label: 'Tags',                placeholder: 'Search tags…',             pricingParam: 'tags',               payloadField: 'tags_ids',               kind: 'flat' },
    { id: 'si-sc',   label: 'Source categories',   placeholder: 'Search categories…',       pricingParam: 'source_categories',  payloadField: 'source_categories_ids',  kind: 'flat' },
    { id: 'si-th',   label: 'Thesaurus / classes', placeholder: 'Search classes…',          pricingParam: 'thesaurus_classes',  payloadField: 'thesaurus_classes_ids',  kind: 'flat' },
    { id: 'si-ss',   label: 'Saved searches',      placeholder: 'Search saved searches…',   pricingParam: 'saved_searches',     payloadField: 'saved_searches_ids',     kind: 'flat' },
    { id: 'si-si',   label: 'Source IDs',          placeholder: 'Search source IDs…',       pricingParam: 'source_ids',         payloadField: 'source_ids',         kind: 'flat' },
  ];

  // Shared verbatim with /search and /image see utils/result-fields.ts.
  readonly FIELD_META: FieldMeta[] = RESULT_FIELD_META;

  readonly SORT_OPTIONS = SORT_OPTIONS;

  readonly MIN_RESULTS = 25;
  readonly MAX_RESULTS = 250;
  readonly RESULTS_STEP = 5;

  readonly MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  readonly DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  // Shared verbatim with /search and /image see utils/query-placeholder.ts.
  readonly QUERY_PLACEHOLDER = QUERY_PLACEHOLDER;

  form: FormGroup;

  private buildForm(): FormGroup {
    return this.fb.group({
      query: ['', Validators.required],
      // Neither starts pre-selected same "nothing pre-populated"
      // convention as every filter row across these pages.
      countries: this.fb.array<string>([]),
      countriesOp: ['OR' as BoolOp],
      geoAreas: this.fb.array<string>([]),
      cikisiOnly: [this.isCikisiUser],
      // Its own field, a sibling of cikisiOnly rather than folded into
      // the Knowledge Topics row see buildCikisiFields()/
      // toggleKnowledgeBase().
      knowledgeBase: [false],
      filterRows: this.fb.array(this.FILTER_ROW_META.map(() => this.buildFilterRowGroup())),
      sortBy: ['relevance'],
      maxResults: [100],
      fields: buildFieldsGroup(this.fb, this.FIELD_META),
    });
  }

  private buildFilterRowGroup(): FormGroup {
    return this.fb.group({
      expanded: [false],
      chips: this.fb.control<MilaMultiSelectOption[]>([]),
    });
  }

  get filterRowsArray(): FormArray {
    return this.form.get('filterRows') as FormArray;
  }

  get countriesArray(): FormArray {
    return this.form.get('countries') as FormArray;
  }

  get geoAreasArray(): FormArray {
    return this.form.get('geoAreas') as FormArray;
  }

  /** What MilaMultiSelect(Hierarchy) binds to on 'flat'/'hierarchy' rows. */
  rowSelectControlOf(rowCtrl: AbstractControl): FormControl<MilaMultiSelectOption[]> {
    return (rowCtrl as FormGroup).get('chips') as FormControl<MilaMultiSelectOption[]>;
  }

  /** Selected-count badge works for either row shape. */
  rowChipCount(rowCtrl: AbstractControl): number {
    return rowCtrl.value.chips?.length ?? 0;
  }

  // Deliberately doesn't clear chips on uncheck see buildCikisiFields().
  toggleRow(rowCtrl: AbstractControl): void {
    const expanded = (rowCtrl as FormGroup).get('expanded')!;
    expanded.setValue(!expanded.value);
  }

  /** Ignores the click entirely while Knowledge Base is on the Knowledge
   *  Topics row is disabled (see toggleKnowledgeBase()), so there's
   *  nothing to toggle. */
  onFilterRowHeaderClick(meta: FilterRowMeta, rowCtrl: AbstractControl): void {
    if (meta.id === 'si-kt' && this.form.value.knowledgeBase) return;
    // Phones pick in a bottom sheet instead of the inline picker; the row
    // turns on once something is picked.
    if (isPhoneViewport()) {
      openCikisiFilterSheet(this.modalService, meta, this.flatFetchers[meta.id], rowCtrl);
      return;
    }
    this.toggleRow(rowCtrl);
  }

  private get knowledgeTopicsRow(): FormGroup {
    const i = this.FILTER_ROW_META.findIndex(m => m.id === 'si-kt');
    return this.filterRowsArray.at(i) as FormGroup;
  }

  /** Checking this means "search the whole knowledge base" collapses
   *  and disables the Knowledge Topics row's own picker (picking specific
   *  topics doesn't mean anything once the whole thing is in scope), same
   *  as unchecking it re-enables that picker without restoring its
   *  expanded state or clearing whatever was already picked there. */
  toggleKnowledgeBase(): void {
    const control = this.form.get('knowledgeBase')!;
    const next = !control.value;
    control.setValue(next);

    const topicsRow = this.knowledgeTopicsRow;
    const chips = topicsRow.get('chips')!;
    if (next) {
      topicsRow.get('expanded')!.setValue(false);
      chips.disable();
    } else {
      chips.enable();
    }
  }

  /** Shared Enter/comma-to-add, Backspace-to-pop-last handler for any chip FormArray. */
  private pushChipFromInput(event: KeyboardEvent, chips: FormArray): void {
    const input = event.target as HTMLInputElement;
    if ((event.key === 'Enter' || event.key === ',') && input.value.trim()) {
      event.preventDefault();
      chips.push(this.fb.control(input.value.trim()));
      input.value = '';
    }
    if (event.key === 'Backspace' && !input.value && chips.length) {
      chips.removeAt(chips.length - 1);
    }
  }

  onCountriesKey(event: KeyboardEvent): void {
    this.pushChipFromInput(event, this.countriesArray);
  }

  removeCountryChip(i: number): void {
    this.countriesArray.removeAt(i);
  }

  onGeoKey(event: KeyboardEvent): void {
    this.pushChipFromInput(event, this.geoAreasArray);
  }

  removeGeoChip(i: number): void {
    this.geoAreasArray.removeAt(i);
  }

  countriesOpen = false;

  toggleCountriesOp(event: MouseEvent): void {
    event.stopPropagation();
    const wasOpen = this.countriesOpen;
    this.closeAll();
    if (!wasOpen) {
      this.countriesOpen = true;
    }
  }

  selectCountriesOp(op: BoolOp, event: MouseEvent): void {
    event.stopPropagation();
    this.form.get('countriesOp')!.setValue(op);
    this.countriesOpen = false;
  }

  // ── Results settings ─────────────────────────────────────────────────
  incrementResults(): void {
    const ctrl = this.form.get('maxResults')!;
    ctrl.setValue(Math.min(this.MAX_RESULTS, ctrl.value + this.RESULTS_STEP));
  }

  decrementResults(): void {
    const ctrl = this.form.get('maxResults')!;
    ctrl.setValue(Math.max(this.MIN_RESULTS, ctrl.value - this.RESULTS_STEP));
  }

  // ── Pricing ──────────────────────────────────────────────────────────
  /** Real backend param names currently in use, for surcharge lookup
   *  against the live catalog (see utils/endpoint-pricing.ts). */
  private get activeParamNames(): string[] {
    const v = this.form.getRawValue();
    const active: string[] = [];
    if (v.knowledgeBase) active.push('knowledge_base');
    this.FILTER_ROW_META.forEach((meta, i) => {
      const row = v.filterRows?.[i];
      if (row?.expanded && row.chips?.length) active.push(meta.pricingParam);
    });
    return active;
  }

  /** Live price estimate, recomputed from current form state on every
   *  change against the real catalog see utils/endpoint-pricing.ts. */
  get estimatedPrice(): PriceEstimate {
    return estimateEndpointPrice(this.endpointDef(), this.activeParamNames);
  }

  get requestSummary(): string {
    return (this.form.value.query ?? '').trim();
  }

  /** An example from the intro (utils/endpoint-intros.ts) into the query. */
  useExample(text: string): void {
    this.form.patchValue({ query: text });
  }

  get codeSnippet(): string {
    return buildCodeSnippets('similar', 'similar', this.buildPayload())[this.activeLanguage];
  }

  setLanguage(lang: CodeLanguage): void {
    this.activeLanguage = lang;
  }

  copySnippet(): void {
    navigator.clipboard?.writeText(this.codeSnippet).then(() => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    });
  }

  // ── Calendar Indexed between ───────────────────────────────────────
  calFrom: CalState = this.newCal();
  calTo: CalState = this.newCal();

  private newCal(): CalState {
    const now = new Date();
    return { open: false, year: now.getFullYear(), month: now.getMonth(), selected: null };
  }

  toggleCal(cal: CalState, event: MouseEvent, title = 'Date'): void {
    event.stopPropagation();
    // Phones pick the date in a bottom sheet instead of the dropdown.
    if (isPhoneViewport()) {
      this.closeAll();
      openDateSheet(this.modalService, title, cal);
      return;
    }
    const wasOpen = cal.open;
    this.closeAll();
    if (!wasOpen) {
      cal.open = true;
    }
  }


  closeAll(): void {
    this.calFrom.open = false;
    this.calTo.open = false;
    this.operatorsHelpOpen = false;
    this.countriesOpen = false;
  }

  closeOperatorsHelp(): void {
    this.operatorsHelpOpen = false;
  }

  /** Bound to (appClickOutside) on the countries panel's own wrapper
   *  see closeCal() for why this closes only its own state. */
  closeCountries(): void {
    this.countriesOpen = false;
  }

  /** Bound to (appClickOutside) on each calendar wrapper: closes just that
   *  calendar, not every dropdown on the page. ClickOutsideDirective fires
   *  on mousedown, before click. Wiring the wrappers to the blanket
   *  closeAll() means clicking a day inside a calendar would register as
   *  "outside" the other wrappers too: their listeners would fire on that
   *  same mousedown and tear the calendar's DOM out (via closeAll()) before
   *  the browser ever dispatches the click, so selectDay() would silently
   *  never run. Each wrapper closing only its own state avoids that. */
  closeCal(cal: CalState): void {
    cal.open = false;
  }

  prevMonth(cal: CalState, event: MouseEvent): void {
    event.stopPropagation();
    if (cal.month === 0) { cal.month = 11; cal.year--; } else cal.month--;
  }

  nextMonth(cal: CalState, event: MouseEvent): void {
    event.stopPropagation();
    if (cal.month === 11) { cal.month = 0; cal.year++; } else cal.month++;
  }

  calDays(cal: CalState): (number | null)[] {
    const first = new Date(cal.year, cal.month, 1).getDay();
    const count = new Date(cal.year, cal.month + 1, 0).getDate();
    const cells: (number | null)[] = Array(first).fill(null);
    for (let d = 1; d <= count; d++) cells.push(d);
    while (cells.length % 7) cells.push(null);
    return cells;
  }

  selectDay(cal: CalState, day: number | null, event: MouseEvent): void {
    event.stopPropagation();
    if (!day) return;
    cal.selected = new Date(cal.year, cal.month, day);
    cal.open = false;
  }

  /** The × inside a date field that has a value. */
  clearDate(cal: CalState, event: MouseEvent): void {
    event.stopPropagation();
    cal.selected = null;
    cal.open = false;
  }

  isSelected(cal: CalState, day: number | null): boolean {
    return !!(day && cal.selected &&
      cal.selected.getDate() === day &&
      cal.selected.getMonth() === cal.month &&
      cal.selected.getFullYear() === cal.year);
  }

  isToday(cal: CalState, day: number | null): boolean {
    if (!day) return false;
    const t = new Date();
    return t.getDate() === day && t.getMonth() === cal.month && t.getFullYear() === cal.year;
  }

  formatDate(cal: CalState): string {
    if (!cal.selected) return '';
    return cal.selected.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private isoDate(d: Date | null): string | null {
    return d ? d.toISOString().slice(0, 10) : null;
  }

  // ── Run query ────────────────────────────────────────────────────────
  private buildPayload() {
    const v = this.form.getRawValue();

    // One boolean per field (include_url, include_domain_name, ...) directly
    // on `response` rather than nested under a `fields` key matches /search's
    // own wire contract (see that page's buildPayload()) exactly the same
    // field set, same include_* naming, same flat placement.
    const fields = flattenFields(this.FIELD_META, v.fields);

    return {
      request: {
        query: v.query,
        countries: v.countries,
        countries_operator: v.countriesOp,
        geolocated_areas: v.geoAreas,
        // {} entirely for a non-Cikisi org not cikisi_content_only/the
        // filter fields with empty values. See buildCikisiFields().
        ...buildCikisiFields(this.isCikisiUser, 'cikisi_content_only', v.cikisiOnly, v.knowledgeBase, this.FILTER_ROW_META, v.filterRows),
        indexed_from: this.isoDate(this.calFrom.selected),
        indexed_to: this.isoDate(this.calTo.selected),
        sort_by: v.sortBy,
        max_results: v.maxResults,
      },
      response: {
        ...fields,
      },
    };
  }

  runQuery(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notificationService.error('Enter a query before running the search.');
      return;
    }

    // The Run button is disabled for this already (see playground.html) this is
    // just a backstop, since the backend can only 422 without a key to bill.
    if (!this.apiKey.keys().length) {
      this.notificationService.error('Create an API key before running this query.');
      return;
    }

    const payload = this.buildPayload();

    this.loadingFields = new Set(Object.entries(payload.response)
      .filter(([key, on]) => key.startsWith('include_') && on === true)
      .map(([key]) => key));
    this.isLoading = true;
    this.errorMessage = null;
    this.playgroundService.runSimilar(payload, this.apiKey.selectedId() ?? undefined)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.status = 'error';
          this.response = null;
          this.runInfo = null;
          // A failed query (body.success false) arrives as an Error carrying the
          // API's messages; a transport failure as an HttpErrorResponse.
          this.errorMessage = error instanceof HttpErrorResponse
            ? apiErrorMessage(error, 'The request failed. Please try again later.')
            : error?.message || 'The query did not succeed.';
          this.notificationService.error('An error occurred while running the query. Please try again later.');
          return throwError(error);
        }),
        tap(({ result, latencyMs, credits, run }) => {
          this.response = result;
          this.latencyMs = latencyMs;
          this.credits = credits;
          this.runInfo = run;
          this.status = 'ok';
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  /** Called from the shared Playground footer's Reset button (see
   *  playground.ts/.html) once the user confirms the confirmation
   *  prompt itself lives there, not here, since it's one shared footer
   *  for all seven playground pages. */
  resetForm(): void {
    this.form = this.buildForm();
    this.status = 'idle';
    this.response = null;
    this.latencyMs = null;
    this.credits = null;
    this.runInfo = null;
    this.errorMessage = null;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
